const GRACE_THRESHOLD = 10;
const OVERAGE_BLOCK_SIZE = 1000;

const initializedStrips = new WeakSet();

const readNumber = value => {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : 0;
};

const planRates = element => ({
    base:          readNumber(element.dataset.base),
    perOrderCents: readNumber(element.dataset.perOrderCents),
    included:      readNumber(element.dataset.included),
    overageRate:   readNumber(element.dataset.overageRate),
    storeFee:      element.dataset.storeFee === '1',
});

const estimateForPlan = (rates, orders, products, stores) => {
    const base = rates.base * (rates.storeFee ? stores : 1);
    const perOrders = (rates.perOrderCents / 100) * orders;
    const overflow = Math.max(0, products - (rates.included * stores));

    const overage = overflow > GRACE_THRESHOLD
        ? Math.ceil((overflow - GRACE_THRESHOLD) / OVERAGE_BLOCK_SIZE) * rates.overageRate
        : 0;

    return base + perOrders + overage;
};

const formatCurrency = amount => '$' + Math.round(amount).toLocaleString();

const sliderValue = (strip, name) => {
    const slider = strip.querySelector(`input[type="range"][data-slider="${name}"]`);

    return slider ? readNumber(slider.value) : readNumber(strip.dataset[name]);
};

const updateCards = recommendedPlan => {
    document.querySelectorAll('.plan-card[data-plan]').forEach(card => {
        const isRecommended = card.dataset.plan === recommendedPlan;
        card.classList.toggle('recommended', isRecommended);

        const cta = card.querySelector('[data-estimate-cta]');

        if (cta) {
            cta.classList.toggle('primary', isRecommended);
            cta.classList.toggle('default', !isRecommended);
        }
    });
};

const recompute = strip => {
    const orders = sliderValue(strip, 'orders');
    const products = sliderValue(strip, 'products');
    const stores = sliderValue(strip, 'stores');

    let recommendedPlan = null;
    let cheapest = Infinity;

    const values = [...strip.querySelectorAll('.plan-estimate-value[data-plan]')].map(element => {
        const amount = estimateForPlan(planRates(element), orders, products, stores);

        if (amount < cheapest) {
            cheapest = amount;
            recommendedPlan = element.dataset.plan;
        }

        return {element, amount};
    });

    values.forEach(({element, amount}) => {
        element.querySelector('[data-estimate-amount]').textContent = formatCurrency(amount);
        element.classList.toggle('recommended', element.dataset.plan === recommendedPlan);
    });

    updateCards(recommendedPlan);
};

const updateSliderLabel = (strip, slider) => {
    const label = strip.querySelector(`[data-slider-value="${slider.dataset.slider}"]`);

    if (label) {
        label.textContent = readNumber(slider.value).toLocaleString();
    }
};

const restoreActualUsage = strip => {
    strip.querySelectorAll('input[type="range"][data-slider]').forEach(slider => {
        slider.value = strip.dataset[slider.dataset.slider];
        updateSliderLabel(strip, slider);
    });

    let recommendedPlan = null;

    strip._planEstimatorDefaults.forEach(snapshot => {
        const element = strip.querySelector(`.plan-estimate-value[data-plan="${snapshot.plan}"]`);
        element.querySelector('[data-estimate-amount]').textContent = snapshot.amount;
        element.classList.toggle('recommended', snapshot.recommended);

        if (snapshot.recommended) {
            recommendedPlan = snapshot.plan;
        }
    });

    updateCards(recommendedPlan);
};

const toggleCustomize = strip => {
    strip.querySelectorAll('[data-tippy-content]').forEach(element => element._tippy?.hide());

    if (strip.classList.toggle('customizing')) {
        return;
    }

    restoreActualUsage(strip);
};

export function initializePlanEstimator(element) {
    element.querySelectorAll('.plan-estimate-strip[data-plan-estimator]').forEach(strip => {
        if (initializedStrips.has(strip)) {
            return;
        }

        initializedStrips.add(strip);

        strip._planEstimatorDefaults = [...strip.querySelectorAll('.plan-estimate-value[data-plan]')].map(value => ({
            plan:        value.dataset.plan,
            amount:      value.querySelector('[data-estimate-amount]').textContent,
            recommended: value.classList.contains('recommended'),
        }));

        strip.querySelectorAll('input[type="range"][data-slider]').forEach(slider => {
            const value = readNumber(slider.value);
            const step = readNumber(slider.step) || 1;

            if (value > readNumber(slider.max)) {
                slider.max = Math.ceil((value * 1.5) / step) * step;
            }

            updateSliderLabel(strip, slider);

            slider.addEventListener('input', () => {
                updateSliderLabel(strip, slider);
                recompute(strip);
            });
        });

        strip.querySelectorAll('[data-estimate-toggle]').forEach(toggle => {
            toggle.addEventListener('click', () => toggleCustomize(strip));
        });
    });
}
